import { type ReactNode, useId } from "react";

import {
  AlertDialog,
  Button,
  Callout,
  Flex,
  Spinner,
  Text,
} from "@radix-ui/themes";

import type { RiskControlKey } from "~/v3-poc/api/riskControls";
import { RiskControlOptionPresentation } from "./RiskControlOption.pres";
import {
  RISK_CONTROL_KEYS,
  RISK_CONTROL_META,
  type RiskControlSelection,
  type RiskControlsDialogMode,
  type RiskControlsDialogView,
} from "./riskControls.model";

export interface RiskControlsDialogPresentationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: RiskControlsDialogMode;
  view: RiskControlsDialogView;
  activeControl: RiskControlKey | null;
  selection: RiskControlSelection;
  canApply: boolean;
  isLoading: boolean;
  isPending: boolean;
  loadErrorMessage?: string;
  submitErrorMessage?: string;
  onControlChange: (control: RiskControlKey, checked: boolean) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onConfirmLift: () => void;
  liquidateAll: ReactNode;
}

export const RiskControlsDialogPresentation = ({
  open,
  onOpenChange,
  mode,
  view,
  activeControl,
  selection,
  canApply,
  isLoading,
  isPending,
  loadErrorMessage,
  submitErrorMessage,
  onControlChange,
  onSubmit,
  onConfirmLift,
  liquidateAll,
}: RiskControlsDialogPresentationProps) => {
  const formId = useId();
  const activeMeta = activeControl ? RISK_CONTROL_META[activeControl] : null;
  const isLift = mode === "lift" && activeMeta !== null;
  const isLiquidating = view === "liquidate";

  const title = isLiquidating
    ? "Liquidate all positions?"
    : isLift
      ? activeMeta.liftTitle
      : "Change risk controls";

  const errorCallout = submitErrorMessage ? (
    <Callout.Root color="red" role="alert">
      <Callout.Text>{submitErrorMessage}</Callout.Text>
    </Callout.Root>
  ) : null;

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Content maxWidth="520px" data-testid="risk-controls-dialog">
        <AlertDialog.Title>{title}</AlertDialog.Title>

        {isLiquidating ? (
          liquidateAll
        ) : (
          <>
            {isLoading ? (
              <Flex
                align="center"
                justify="center"
                gap="2"
                py="6"
                role="status"
              >
                <Spinner />
                <Text size="2" color="gray">
                  Loading current settings…
                </Text>
              </Flex>
            ) : loadErrorMessage ? (
              <Callout.Root color="red" role="alert" mt="4">
                <Callout.Text>{loadErrorMessage}</Callout.Text>
              </Callout.Root>
            ) : isLift ? (
              <Flex direction="column" gap="3" mt="4">
                <Text size="2">{activeMeta.liftEffect}</Text>
                {errorCallout}
              </Flex>
            ) : (
              <form id={formId} onSubmit={onSubmit}>
                <Flex direction="column" gap="3" mt="4">
                  {RISK_CONTROL_KEYS.map((key) => (
                    <RiskControlOptionPresentation
                      key={key}
                      label={RISK_CONTROL_META[key].label}
                      description={RISK_CONTROL_META[key].description}
                      checked={selection === key}
                      disabled={isPending}
                      onCheckedChange={(checked) =>
                        onControlChange(key, checked)
                      }
                    />
                  ))}

                  {errorCallout}
                </Flex>
              </form>
            )}

            {liquidateAll}

            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button
                  type="button"
                  variant="soft"
                  color="gray"
                  disabled={isPending}
                >
                  Cancel
                </Button>
              </AlertDialog.Cancel>
              {/*
                Deliberately not an AlertDialog.Action: that closes on click, and
                the dialog has to stay open until the PUT resolves so a failure
                is shown here rather than behind a dismissed dialog.
              */}
              {isLift ? (
                <Button
                  type="button"
                  disabled={isPending}
                  loading={isPending}
                  onClick={onConfirmLift}
                >
                  {activeMeta.liftConfirmLabel}
                </Button>
              ) : (
                <Button
                  type="submit"
                  form={formId}
                  color="red"
                  disabled={!canApply || isPending}
                  loading={isPending}
                >
                  Apply change
                </Button>
              )}
            </Flex>
          </>
        )}
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
};
